import { CheckBoxWithLabelProps } from '@tableau/widgets';
import * as _ from 'underscore';
import { Messages } from '../../codegen/Localize';
import { ISiteRolePickerMenuScope, SiteRolePickerMenuHelper } from '../helpers/SiteRolePickerMenuHelper';
import { Module } from '../Module';
import { Groups } from '../resources/Groups';
import { DefaultSiteRole, SiteRoleOption, SiteRoleOptionsResource } from '../resources/SiteRoleOptions';
import { SiteRolesResource } from '../resources/SiteRoles';
import { GrantLicenseModes } from '../ServerApi/requests/SyncActiveDirectoryGroupRequest';
import { IResult } from '../ServerApi/types/IResponse';
import { ISiteRole, SiteRoles } from '../ServerApi/types/ISiteRole';
import { ConfirmActionDialog, IConfirmActionDialogInstance } from './ConfirmActionDialog';
import { CreateGroupActionNotification } from './GroupsActionNotification';
import { ServerService } from './ServerService';

export interface ICreateGroupDialogScope extends ng.IScope, ISiteRolePickerMenuScope {
  description: string;
  input: {
    text: string;
    maxLength?: number;
  };

  // for the grant license mode checkbox
  grantLicenseMode?: GrantLicenseModes;
  licenseUponLoginCheckboxProps: CheckBoxWithLabelProps;

  // for the site role selector
  siteRoleOptions: string[];
  minimumSiteRole?: string;
}

export class CreateGroupDialogAction {

  public static $inject = ['$translate',
                           'ConfirmActionDialog',
                           'CreateGroupActionNotification',
                           'Groups',
                           'ServerService',
                           'SiteRoles',
                           'SiteRoleOptionsResource'];

  constructor(
    private $translate: ng.translate.ITranslateService,
    private ConfirmActionDialog: ConfirmActionDialog,
    private createGroupActionNotification: CreateGroupActionNotification,
    private Groups: Groups,
    private ServerService: ServerService,
    private siteRoles: SiteRolesResource,
    private siteRoleOptionsResource: SiteRoleOptionsResource) { }

  public showCreateGroupDialog(parentScope: ng.IScope): ng.IPromise<IResult> {

    const dialogScope = <ICreateGroupDialogScope> parentScope.$new();

    dialogScope.description = this.$translate.instant('createGroupDesc');
    dialogScope.input = {
      text: '',
      maxLength: 255
    };
    dialogScope.grantLicenseMode = GrantLicenseModes.ON_SYNC;
    dialogScope.minimumSiteRole = undefined;
    dialogScope.siteRoleOptions = this.siteRoles.optionsForCurrentUserExcept(SiteRoles.ServerAdministrator);

    const onSiteRoleSelected = (roleOption: SiteRoleOption) => {
      dialogScope.$apply(() => {
        dialogScope.siteRoleSelectorProps.selectedRole = roleOption;
        dialogScope.minimumSiteRole = roleOption.role;
        let isUnlicensedSiteRole = dialogScope.minimumSiteRole === SiteRoles.Unlicensed;
        dialogScope.grantLicenseMode =
          isUnlicensedSiteRole ? GrantLicenseModes.ON_SYNC : dialogScope.grantLicenseMode;
        dialogScope.licenseUponLoginCheckboxProps =
          {...dialogScope.licenseUponLoginCheckboxProps,
              disabled: isUnlicensedSiteRole,
              checked: dialogScope.grantLicenseMode === GrantLicenseModes.ON_LOGIN
          };
      });
    };

    SiteRolePickerMenuHelper.setupPickerMenuScope(dialogScope, this.$translate, this.ServerService,
      this.siteRoleOptionsResource, DefaultSiteRole, onSiteRoleSelected, true, Messages.minimumSiteRole());
    dialogScope.siteRoleSelectorProps.isDisabled = true;
    SiteRolePickerMenuHelper.updatePickerMenuDropdownOptionDisabledStates(dialogScope,
      (option: SiteRoleOption) => option.role === SiteRoles.Unlicensed);

    const toggleLicenseUponLogin = (checked: boolean) => {
      dialogScope.grantLicenseMode =
        checked ? GrantLicenseModes.ON_LOGIN : GrantLicenseModes.ON_SYNC;
      dialogScope.licenseUponLoginCheckboxProps =
        {...dialogScope.licenseUponLoginCheckboxProps,
            checked: checked
        };
      dialogScope.siteRoleSelectorProps.isDisabled = !checked;
      dialogScope.minimumSiteRole = checked ? DefaultSiteRole : undefined;
      dialogScope.$digest();
    };

    dialogScope.licenseUponLoginCheckboxProps = {
      checked: false,
      handleChange: toggleLicenseUponLogin.bind(this),
      containerStyle: {marginLeft: '0px', marginTop: '28px', marginBottom: '16px'},
      label: Messages.grantLicenseUponLoginCheckboxLabel(),
      testId: 'license-upon-login'
    };

    const activeDirectoryEnabled = this.ServerService.isServerDomainFamilyActiveDirectory();

    const dialogInstance = this.ConfirmActionDialog.open({
      title: this.$translate.instant(activeDirectoryEnabled ? 'createLocalGroupTitle' : 'createGroupTitle'),
      bodyTemplateUrl: 'createGroupActionDialog.html',
      scope: dialogScope,
      position: { reserveHeight: 250 },
      confirmDisabled: () => _.isEmpty(dialogScope.input.text),
      actionName: this.$translate.instant('createGroupDialogSubmit'),
      action: (dialogInstance: IConfirmActionDialogInstance) => {
        return this.Groups.createGroup(dialogScope.input.text,
          dialogScope.grantLicenseMode, dialogScope.minimumSiteRole).then((result: IResult) => {
          this.createGroupActionNotification.notify(
            (errorMsg: string) => {
              dialogInstance.showErrorMessage(errorMsg);
            },
            result,
            dialogScope.input.text);
        });
      }
    });
    dialogInstance.result.finally(() => dialogScope.$destroy());
    return dialogInstance.result;
  }

}

Module.instance().service('CreateGroupDialogAction', CreateGroupDialogAction);
